import { Formik, FormikConfig } from 'formik';
import { makeValidator } from 'mandle';
import * as React from 'react';

import { Input } from '../../components/Input';
import { SubmitButton } from '../../components/SubmitButton';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useTitle } from '../../useTitle';
import { makeInputPropsMaker } from '../../utils/inputPropsMaker';
import { emailAddress, required2 } from '../../validators';
import { useForgotPasswordMutation } from '../../generated/graphql';
import { formatErrors } from '../../utils/formatErrors';
import { LoginLayout } from '../../layouts/LoginLayout';
import { isNil } from '../../utils/isNil';
import { SignUpPrompt } from '../Auth/SignupPrompt';

interface Fields {
  email: string;
}

const validate = makeValidator<Fields>({
  email: [required2('Enter your email address'), emailAddress],
});

export const ForgotPassword: React.FC = () => {
  const analytics = useAnalytics();
  useTitle('Forgot Password');
  const [requestForgotEmailResponse, requestForgotEmail] =
    useForgotPasswordMutation();

  if (
    requestForgotEmailResponse.data &&
    isNil(requestForgotEmailResponse.data?.forgotPassword?.validation)
  ) {
    return (
      <LoginLayout>
        <ForgotPasswordConfirmation />
      </LoginLayout>
    );
  }

  const submitForm: FormikConfig<Fields>['onSubmit'] = (variables, actions) => {
    analytics.track('Form Submitted', {
      form: 'forgot_password',
    });
    requestForgotEmail(variables).then((res) => {
      if (res.data?.forgotPassword?.validation) {
        actions.setErrors(formatErrors(res.data.forgotPassword.validation));
      }
    });
  };

  return (
    <LoginLayout footer={<SignUpPrompt />}>
      <h1 className="text-center font-semibold mb-2">Forgot password</h1>
      <p className="text-grey-700 mb-4 text-center">
        Enter your email and we'll send you a link to reset your password.
      </p>
      <Formik
        initialValues={{ email: '' }}
        onSubmit={submitForm}
        validate={validate}
      >
        {(formProps) => {
          const getInputProps = makeInputPropsMaker(formProps);
          return (
            <form
              noValidate={true}
              onSubmit={formProps.handleSubmit}
              className="space-y-6"
            >
              <Input
                label="Your email address"
                type="email"
                {...getInputProps('email')}
              />

              <SubmitButton loading={requestForgotEmailResponse.fetching}>
                Send password reset link
              </SubmitButton>
            </form>
          );
        }}
      </Formik>
    </LoginLayout>
  );
};

const ForgotPasswordConfirmation: React.FC = () => {
  return (
    <div className="text-center">
      <h1 className="font-semibold mb-4">Check your inbox</h1>
      <div className="space-y-4 text-grey-700">
        <p>Don't worry, it happens to the best of us!</p>

        <p>
          If you have a Matchhaus account we've sent you an email with a link to
          reset your password.
        </p>
        <p>
          If you don't see it in your inbox, check your spam. If you still don't
          see it, make sure you entered the correct email address.
        </p>
      </div>
    </div>
  );
};
